import React, { forwardRef, useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import Icon from './Icon'
import Input from './Input'

type SearchInputProps = React.ComponentProps<typeof Input> & {
  CustomInput?: React.ComponentType<React.ComponentProps<typeof Input>>
  customInputProps?: Record<string, unknown>
}

const InputContainer = styled.div`
  position: relative;
  display: grid;

  input {
    padding-left: 3rem;
  }

  input::-ms-reveal,
  input::-ms-clear {
    display: none;
  }

  // hide the x on search bar
  [type='search']::-webkit-search-cancel-button,
  [type='search']::-webkit-search-decoration,
  [type='search']::-webkit-search-results-button,
  [type='search']::-webkit-search-results-decoration {
    display: none;
  }
`

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0.5rem;
  display: grid;
  place-items: center;
  pointer-events: none;
`

const IconTarget = styled.div<{
  iconColor: string
}>`
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  background: transparent;
  border: none;
  padding: 0;
  color: ${props => props.theme.colors.tertiaryIcon};
  font-size: 1.5rem;

  outline-style: none;
`

const DefaultInput = styled(Input)`
  min-height: 50px;
  border-radius: 2.5rem;
`

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ CustomInput = DefaultInput, customInputProps, ...props }, ref) => {
    const [isInputFocused, setInputFocused] = useState(false)
    const theme = useTheme()

    const iconColor = (() => {
      if (props.invalid) {
        return theme.colors.error
      }
      if (props.disabled) {
        return theme.colors.disabledIcon
      }
      if (isInputFocused) {
        return theme.colors.primary
      }
      return theme.colors.label
    })()

    const inputProps = { ...customInputProps, ...props }

    return (
      <InputContainer role="search">
        <IconContainer>
          <IconTarget iconColor={iconColor}>
            <Icon name="search" />
          </IconTarget>
        </IconContainer>
        <CustomInput
          role="searchbox"
          {...inputProps}
          onFocus={(event: React.FocusEvent<HTMLInputElement, Element>) => {
            props.onFocus?.(event)
            setInputFocused(true)
          }}
          onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
            props.onBlur?.(event)
            setInputFocused(false)
          }}
          ref={ref}
        />
      </InputContainer>
    )
  }
)

export default SearchInput
